import { readFileSync } from 'node:fs';
import path, { basename, extname, join } from 'node:path';
import {
  ScriptPlayer,
  parseYamlScript,
  resolveWebTarget,
} from '@midscene/core/yaml';
import { createServer } from 'http-server';

import assert from 'node:assert';
import type {
  AgentOpt,
  FreeFn,
  MidsceneYamlScript,
  MidsceneYamlScriptAgentOpt,
  MidsceneYamlScriptEnv,
  MidsceneYamlScriptWebEnv,
} from '@midscene/core';
import { createAgent, getReportFileName } from '@midscene/core/agent';
import type { AbstractInterface } from '@midscene/core/device';
import { processCacheConfig } from '@midscene/core/utils';
import { getDebug } from '@midscene/shared/logger';
import { AgentOverChromeBridge } from '@midscene/web/bridge-mode';
import {
  buildDownloadBehavior,
  puppeteerAgentForTarget,
} from '@midscene/web/puppeteer-agent-launcher';
import type { Browser, BrowserContext, Page } from 'puppeteer';
import puppeteer from 'puppeteer';

export interface SingleYamlExecutionResult {
  success: boolean;
  file: string;
  player: ScriptPlayer<MidsceneYamlScriptEnv>;
}

export interface CreateYamlPlayerOptions {
  headed?: boolean;
  keepWindow?: boolean;
  browser?: Browser;
  page?: Page;
  browserContext?: BrowserContext;
  testId?: string;
}

const debug = getDebug('create-yaml-player');
const cleanupWarning = getDebug('create-yaml-player', { console: true });

async function cleanupFailedPlayerSetup(freeFn: FreeFn[]): Promise<void> {
  for (const cleanup of [...freeFn].reverse()) {
    try {
      await cleanup.fn();
    } catch (error) {
      cleanupWarning(
        `failed to run ${cleanup.name} after YAML player setup failed`,
        error,
      );
    }
  }
}

export const launchServer = async (
  dir: string,
): Promise<ReturnType<typeof createServer>> => {
  // https://github.com/http-party/http-server/blob/master/bin/http-server
  return new Promise((resolve) => {
    const server = createServer({
      root: dir,
    });
    server.listen(0, '127.0.0.1', () => {
      resolve(server);
    });
  });
};

/**
 * Resolves reportFileName with proper priority handling.
 * Priority: YAML reportFileName > CLI testId (legacy) > YAML testId (legacy) > fileName
 * Explicit YAML reportFileName is treated as the exact output name. Generated
 * legacy fallbacks include a unique suffix to avoid overwriting.
 */
function resolveReportFileName(
  yamlReportFileName: string | undefined,
  cliTestId: string | undefined,
  yamlTestId: string | undefined,
  fileName: string,
): string {
  if (yamlReportFileName !== undefined) {
    return yamlReportFileName;
  }
  const baseName = cliTestId ?? yamlTestId ?? fileName;
  return getReportFileName(baseName);
}

/**
 * Builds agent options by merging YAML agent config with processed cache and report name.
 * Handles the spread of agent options and ensures proper cache configuration.
 */
function buildAgentOptions(
  yamlAgent: MidsceneYamlScriptAgentOpt | undefined,
  reportFileName: string,
  fileName: string,
): Partial<AgentOpt> {
  return {
    ...(yamlAgent || {}),
    cache: processCacheConfig(yamlAgent?.cache, fileName),
    reportFileName,
  };
}

export async function createYamlPlayer(
  file: string,
  script?: MidsceneYamlScript,
  options?: CreateYamlPlayerOptions,
): Promise<ScriptPlayer<MidsceneYamlScriptEnv>> {
  const yamlScript =
    script || parseYamlScript(readFileSync(file, 'utf-8'), file);

  // Deep clone the script to avoid mutation issues when the same file is executed multiple times
  // This ensures each ScriptPlayer instance has its own independent copy of the YAML data
  const clonedYamlScript = structuredClone(yamlScript);

  const fileName = basename(file, extname(file));
  const preference = {
    headed: options?.headed,
    keepWindow: options?.keepWindow,
    reportFileName: resolveReportFileName(
      clonedYamlScript.agent?.reportFileName,
      options?.testId,
      clonedYamlScript.agent?.testId,
      fileName,
    ),
  };

  const player = new ScriptPlayer(
    clonedYamlScript,
    async () => {
      const freeFn: FreeFn[] = [];
      try {
        const resolvedWebTarget = resolveWebTarget(clonedYamlScript);
        const webTarget = resolvedWebTarget?.target as
          | MidsceneYamlScriptWebEnv
          | undefined;

        // Validate that only one target type is specified
        const targetCount = [
          typeof resolvedWebTarget !== 'undefined',
          typeof clonedYamlScript.android !== 'undefined',
          typeof clonedYamlScript.ios !== 'undefined',
          typeof clonedYamlScript.harmony !== 'undefined',
          typeof clonedYamlScript.computer !== 'undefined',
          typeof clonedYamlScript.interface !== 'undefined',
        ].filter(Boolean).length;

        if (targetCount > 1) {
          const specifiedTargets = [
            resolvedWebTarget?.source ?? null,
            typeof clonedYamlScript.android !== 'undefined' ? 'android' : null,
            typeof clonedYamlScript.ios !== 'undefined' ? 'ios' : null,
            typeof clonedYamlScript.harmony !== 'undefined' ? 'harmony' : null,
            typeof clonedYamlScript.computer !== 'undefined'
              ? 'computer'
              : null,
            typeof clonedYamlScript.interface !== 'undefined'
              ? 'interface'
              : null,
          ].filter(Boolean);

          throw new Error(
            `Only one target type can be specified, but found multiple: ${specifiedTargets.join(', ')}. Please specify only one of: page, browser, web, android, ios, harmony, computer, or interface.`,
          );
        }

        // handle new web config
        if (typeof webTarget !== 'undefined') {
          if (resolvedWebTarget?.source === 'target') {
            console.warn(
              'target is deprecated, please use page or browser instead. See https://midscenejs.com/automate-with-scripts-in-yaml for more information. Sorry for the inconvenience.',
            );
          }

          // launch local server if needed
          let localServer: Awaited<ReturnType<typeof launchServer>> | undefined;
          let urlToVisit: string | undefined;
          if (webTarget.serve) {
            assert(
              typeof webTarget.url === 'string',
              'url is required in serve mode',
            );
            localServer = await launchServer(webTarget.serve);
            const serverAddress = localServer.server.address();
            freeFn.push({
              name: 'local_server',
              fn: async () => {
                if (!localServer) {
                  return;
                }
                await new Promise<void>((resolve, reject) => {
                  localServer?.server.close((error?: Error) => {
                    if (error) {
                      reject(error);
                    } else {
                      resolve();
                    }
                  });
                });
              },
            });
            if (webTarget.url.startsWith('/')) {
              urlToVisit = `http://${serverAddress?.address}:${serverAddress?.port}${webTarget.url}`;
            } else {
              urlToVisit = `http://${serverAddress?.address}:${serverAddress?.port}/${webTarget.url}`;
            }
            webTarget.url = urlToVisit;
          }

          // Validate: cdpEndpoint and bridgeMode are mutually exclusive
          if (webTarget.cdpEndpoint && webTarget.bridgeMode) {
            throw new Error(
              'cdpEndpoint and bridgeMode are mutually exclusive. Please specify only one.',
            );
          }

          if (webTarget.mode === 'browser' && webTarget.bridgeMode) {
            throw new Error(
              '[midscene] browser mode does not support bridgeMode. Use page: or web.mode: page for bridge mode.',
            );
          }

          // CDP mode: connect to an existing browser via Chrome DevTools Protocol
          if (webTarget.cdpEndpoint) {
            // Use the shared browser from batch-runner if available (shareBrowserContext),
            // otherwise connect via CDP endpoint
            const ownsCdpBrowser = !options?.browser;
            const cdpBrowser =
              options?.browser ??
              (await puppeteer.connect({
                browserWSEndpoint: webTarget.cdpEndpoint,
                defaultViewport: null,
                downloadBehavior: buildDownloadBehavior(webTarget.downloadPath),
              }));
            // Warn about options that don't apply to an already-running browser
            if (webTarget.chromeArgs) {
              console.warn(
                'chromeArgs are not supported in CDP mode (browser is already running). They will be ignored.',
              );
            }

            // Reuse puppeteerAgentForTarget which handles page setup (userAgent, viewport,
            // cookie, waitForNetworkIdle, etc.) — pass the CDP browser as the browser param
            let launchedAgent: Awaited<
              ReturnType<typeof puppeteerAgentForTarget>
            >;
            try {
              launchedAgent = await puppeteerAgentForTarget(
                webTarget,
                {
                  ...preference,
                  ...buildAgentOptions(
                    clonedYamlScript.agent,
                    preference.reportFileName,
                    fileName,
                  ),
                },
                cdpBrowser as Browser,
                options?.page,
                options?.browserContext,
              );
            } catch (error) {
              if (ownsCdpBrowser) {
                try {
                  cdpBrowser.disconnect();
                } catch (cleanupError) {
                  cleanupWarning(
                    'failed to disconnect the CDP browser after agent setup failed',
                    cleanupError,
                  );
                }
              }
              throw error;
            }
            const { agent, freeFn: newFreeFn } = launchedAgent;

            // Replace the default browser close with disconnect for CDP
            const cleanFreeFn = newFreeFn.filter(
              (f) => f.name !== 'puppeteer_browser',
            );
            freeFn.push(...cleanFreeFn);
            if (ownsCdpBrowser) {
              freeFn.push({
                name: 'cdp_browser_disconnect',
                fn: () => cdpBrowser.disconnect(),
              });
            }

            return { agent, freeFn };
          }

          if (!webTarget.bridgeMode) {
            // use puppeteer
            const { agent, freeFn: newFreeFn } = await puppeteerAgentForTarget(
              webTarget,
              {
                ...preference,
                ...buildAgentOptions(
                  clonedYamlScript.agent,
                  preference.reportFileName,
                  fileName,
                ),
              },
              options?.browser,
              options?.page,
              options?.browserContext,
            );
            freeFn.push(...newFreeFn);

            return { agent, freeFn };
          }
          assert(
            webTarget.bridgeMode === 'newTabWithUrl' ||
              webTarget.bridgeMode === 'currentTab',
            `bridgeMode config value must be either "newTabWithUrl" or "currentTab", but got ${webTarget.bridgeMode}`,
          );

          const bridgeUnsupportedKeys: (keyof MidsceneYamlScriptWebEnv)[] = [
            'userAgent',
            'viewportWidth',
            'viewportHeight',
            'deviceScaleFactor',
            'waitForNetworkIdle',
            'cookie',
            'extraHTTPHeaders',
            'downloadPath',
            'chromeArgs',
          ];
          const ignoredKeys = bridgeUnsupportedKeys.filter(
            (key) => webTarget[key] != null,
          );
          if (ignoredKeys.length > 0) {
            console.warn(
              `puppeteer options (${ignoredKeys.join(', ')}) are not supported in bridge mode. They will be ignored.`,
            );
          }

          const agent = new AgentOverChromeBridge({
            closeNewTabsAfterDisconnect: webTarget.closeNewTabsAfterDisconnect,
            closeConflictServer: true,
            ...buildAgentOptions(
              clonedYamlScript.agent,
              preference.reportFileName,
              fileName,
            ),
          });

          if (webTarget.bridgeMode === 'newTabWithUrl') {
            await agent.connectNewTabWithUrl(webTarget.url);
          } else {
            if (webTarget.url) {
              console.warn(
                'url will be ignored in bridge mode with "currentTab"',
              );
            }
            await agent.connectCurrentTab();
          }
          freeFn.push({
            name: 'destroy_agent_over_chrome_bridge',
            fn: () => agent.destroy(),
          });
          return {
            agent,
            freeFn,
          };
        }

        // handle android
        if (typeof clonedYamlScript.android !== 'undefined') {
          const androidTarget = clonedYamlScript.android;
          const { agentFromAdbDevice } = await import('@midscene/android');
          const agent = await agentFromAdbDevice(androidTarget?.deviceId, {
            ...androidTarget, // Pass all Android config options
            ...buildAgentOptions(
              clonedYamlScript.agent,
              preference.reportFileName,
              fileName,
            ),
          });

          if (androidTarget?.launch) {
            await agent.launch(androidTarget.launch);
          }

          freeFn.push({
            name: 'destroy_android_agent',
            fn: () => agent.destroy(),
          });

          return { agent, freeFn };
        }

        // handle iOS
        if (typeof clonedYamlScript.ios !== 'undefined') {
          const iosTarget = clonedYamlScript.ios;
          const { agentFromWebDriverAgent } = await import('@midscene/ios');
          const agent = await agentFromWebDriverAgent({
            ...iosTarget, // Pass all iOS config options
            ...buildAgentOptions(
              clonedYamlScript.agent,
              preference.reportFileName,
              fileName,
            ),
          });

          if (iosTarget?.launch) {
            await agent.launch(iosTarget.launch);
          }

          freeFn.push({
            name: 'destroy_ios_agent',
            fn: () => agent.destroy(),
          });

          return { agent, freeFn };
        }

        // handle harmony
        if (typeof clonedYamlScript.harmony !== 'undefined') {
          const harmonyTarget = clonedYamlScript.harmony;
          const { agentFromHdcDevice } = await import('@midscene/harmony');
          const agent = await agentFromHdcDevice(harmonyTarget?.deviceId, {
            ...harmonyTarget, // Pass all HarmonyOS config options
            ...buildAgentOptions(
              clonedYamlScript.agent,
              preference.reportFileName,
              fileName,
            ),
          });

          if (harmonyTarget?.launch) {
            await agent.launch(harmonyTarget.launch);
          }

          freeFn.push({
            name: 'destroy_harmony_agent',
            fn: () => agent.destroy(),
          });

          return { agent, freeFn };
        }

        // handle computer
        if (typeof clonedYamlScript.computer !== 'undefined') {
          const computerTarget = clonedYamlScript.computer;
          const { agentForComputer } = await import('@midscene/computer');
          const agent = await agentForComputer({
            ...computerTarget,
            ...buildAgentOptions(
              clonedYamlScript.agent,
              preference.reportFileName,
              fileName,
            ),
          });

          freeFn.push({
            name: 'destroy_computer_agent',
            fn: () => agent.destroy(),
          });

          return { agent, freeFn };
        }

        // handle general interface
        if (typeof clonedYamlScript.interface !== 'undefined') {
          const interfaceTarget = clonedYamlScript.interface;

          const moduleSpecifier = interfaceTarget.module;
          let finalModuleSpecifier: string;
          if (
            moduleSpecifier.startsWith('./') ||
            moduleSpecifier.startsWith('../') ||
            path.isAbsolute(moduleSpecifier)
          ) {
            const resolvedPath = join(process.cwd(), moduleSpecifier);
            finalModuleSpecifier = resolvedPath;
          } else {
            finalModuleSpecifier = moduleSpecifier;
          }

          // import the module dynamically
          debug(
            'importing module config',
            interfaceTarget.module,
            'with export config',
            interfaceTarget.export,
            'final module specifier',
            finalModuleSpecifier,
          );

          const importedModule = await import(finalModuleSpecifier);

          // get the specific export or use default export
          const DeviceClass = interfaceTarget.export
            ? importedModule[interfaceTarget.export]
            : importedModule.default || importedModule;

          debug(
            'DeviceClass',
            DeviceClass,
            'with param',
            interfaceTarget.param,
          );

          // create device instance with parameters
          const device: AbstractInterface = new DeviceClass(
            interfaceTarget.param || {},
          );

          // create agent from device
          debug('creating agent from device', device);
          const agent = createAgent(
            device,
            buildAgentOptions(
              clonedYamlScript.agent,
              preference.reportFileName,
              fileName,
            ),
          );

          freeFn.push({
            name: 'destroy_general_interface_agent',
            fn: () => {
              agent.destroy();
            },
          });

          return { agent, freeFn };
        }

        throw new Error(
          'No valid interface configuration found in the yaml script, should be either "web", "android", "ios", "harmony", "computer", or "interface"',
        );
      } catch (error) {
        await cleanupFailedPlayerSetup(freeFn);
        throw error;
      }
    },
    undefined,
    file,
  );

  return player;
}
